import {
  authenticationBody, formatIstTime, isAuthentication, renderTemplate, renderWhatsAppText, templateButtons,
  type Template, type TemplateDraft,
} from '@wa/shared';

/**
 * The pieces the Templates screens share: the status and category chips the list,
 * the drawer and the editor all show, and the message drawn the way WhatsApp
 * draws it — once, so the list's preview, the drawer's bubble and the editor's
 * phone cannot disagree about what a template looks like.
 */

/** What every preview needs, whether it comes from a stored template or a draft being typed. */
export interface Shape {
  headerFormat: string;
  headerText: string | null;
  /** A local object URL for an uploaded sample image, when there is one. */
  headerImageUrl?: string | null;
  body: string;
  footer: string | null;
  buttons: { type: string; text: string }[];
  /** Authentication templates: Meta writes the wording; the preview says so. */
  metaWritten?: boolean;
}

export function shapeOf(t: Template): Shape {
  return {
    headerFormat: (t.headerFormat ?? 'NONE').toUpperCase(),
    headerText: t.headerText,
    body: t.bodyText ?? '',
    footer: t.footerText,
    buttons: templateButtons(t).map((b) => ({ type: b.type, text: b.text })),
  };
}

export function shapeOfDraft(draft: TemplateDraft, headerImageUrl?: string | null): Shape {
  const auth = isAuthentication(draft);
  if (auth) {
    return {
      headerFormat: 'NONE',
      headerText: null,
      body: authenticationBody(draft),
      footer: draft.codeExpirationMinutes ? `This code expires in ${draft.codeExpirationMinutes} minutes.` : null,
      buttons: [{
        type: draft.otpType === 'ONE_TAP' ? 'URL' : 'COPY_CODE',
        text: draft.otpButtonText || (draft.otpType === 'ONE_TAP' ? 'Autofill' : 'Copy code'),
      }],
      metaWritten: true,
    };
  }
  return {
    headerFormat: draft.headerFormat,
    headerText: draft.headerFormat === 'TEXT' ? renderTemplate(draft.headerText ?? '', [draft.headerExample ?? '']) : null,
    headerImageUrl,
    body: renderTemplate(draft.body, draft.bodyExamples),
    footer: draft.footer ?? null,
    buttons: draft.buttons.map((b) => ({ type: b.type, text: b.type === 'COPY_CODE' ? (b.example || 'Copy code') : (b.text ?? '') })),
  };
}

/**
 * The message bubble, WhatsApp's way: an outbound bubble with the header bold,
 * the body, the footer small and grey, the time, then the buttons as rows under
 * a hairline. Beyond three, WhatsApp shows the first two and "See all options";
 * the preview does the same so nobody is surprised by the phone.
 */
export function Bubble({ shape, width = 'w-[min(18.5rem,100%)]', bodyClass = '', stamp }: {
  shape: Shape; width?: string; bodyClass?: string; stamp?: string;
}) {
  const buttons = shape.buttons.filter((b) => b.text);
  const shown = buttons.length > 3 ? buttons.slice(0, 2) : buttons;
  const media = shape.headerFormat !== 'NONE' && shape.headerFormat !== 'TEXT';
  return (
    <div className={`${width} overflow-hidden rounded-lg rounded-tl-sm bg-(--color-bubble-in) text-(--color-bubble-ink) shadow-sm`}>
      <div className="px-2.5 pt-2 pb-1.5">
        {media && (
          shape.headerImageUrl && shape.headerFormat === 'IMAGE'
            ? <img src={shape.headerImageUrl} alt="" className="mb-1.5 max-h-40 w-full rounded object-cover" />
            : (
              <div className="mb-1.5 grid h-24 place-items-center rounded bg-black/8 text-(--color-muted)">
                {shape.headerFormat === 'IMAGE' && <IconImage />}
                {shape.headerFormat === 'VIDEO' && <IconVideo />}
                {shape.headerFormat === 'DOCUMENT' && <IconDoc />}
              </div>
            )
        )}
        {shape.headerText && <p className="mb-0.5 text-body font-semibold break-words">{shape.headerText}</p>}
        {shape.body
          ? <p className={`text-body break-words whitespace-pre-wrap ${bodyClass}`}>{renderWhatsAppText(shape.body)}</p>
          : <p className="text-body text-(--color-faint) italic">Your message appears here</p>}
        {shape.metaWritten && <p className="mt-1 text-micro text-(--color-muted) italic">WhatsApp writes and translates this wording.</p>}
        {shape.footer && <p className="mt-1 text-small break-words text-(--color-muted)">{shape.footer}</p>}
        <p className="mt-0.5 text-right text-micro text-(--color-faint)">{stamp ?? formatIstTime(new Date().toISOString())}</p>
      </div>
      {shown.length > 0 && (
        <ul className="border-t border-(--color-line-dim)">
          {shown.map((b, i) => (
            <li key={i} className={`flex items-center justify-center gap-1.5 py-2 text-body font-medium text-(--color-tick-read) ${i > 0 ? 'border-t border-(--color-line-dim)' : ''}`}>
              {b.type === 'URL' && <IconLink />}
              {b.type === 'PHONE_NUMBER' && <IconPhone />}
              {b.type === 'COPY_CODE' && <IconCopy />}
              <span className="truncate">{b.text}</span>
            </li>
          ))}
          {buttons.length > 3 && (
            <li className="flex items-center justify-center gap-1.5 border-t border-(--color-line-dim) py-2 text-body font-medium text-(--color-tick-read)">
              <IconList /> See all options
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * The phone the editor previews on. Drawn in CSS rather than an image so it
 * follows the theme and costs nothing to load; the parts are the ones a person
 * recognises a WhatsApp screen by — the green bar, the wallpaper, the composer.
 */
export function PhonePreview({ shape }: { shape: Shape }) {
  return (
    <div className="mx-auto w-[17.75rem] rounded-[2.2rem] border-[7px] border-[#1a1a1a] bg-[#1a1a1a] shadow-(--shadow-pop)">
      <div className="flex h-[33rem] flex-col overflow-hidden rounded-[1.75rem] bg-(--color-chat-ground)">
        <div className="shrink-0 bg-[#0B6B5B] px-4 pt-2 pb-2 text-white">
          <div className="flex items-center justify-between text-[10px] font-semibold">
            <span>{formatIstTime(new Date().toISOString())}</span>
            <span>99%</span>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M12 4 6 10l6 6" {...s} stroke="#fff" /></svg>
            <span className="size-7 rounded-full bg-white/25" />
            <span className="h-2.5 w-20 rounded bg-white/40" />
          </div>
        </div>
        <div className="chat-ground pane-scroll min-h-0 flex-1 overflow-y-auto bg-size-[120px_120px] p-2.5">
          <Bubble shape={shape} width="w-full" />
        </div>
        <div className="flex shrink-0 items-center gap-2 px-2 pb-2.5 pt-1.5">
          <span className="flex h-9 flex-1 items-center rounded-full bg-(--color-surface) px-3 text-small text-(--color-faint)">Message</span>
          <span className="grid size-9 place-items-center rounded-full bg-[#0B6B5B]">
            <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><rect x="7.5" y="3" width="5" height="9" rx="2.5" fill="#fff" /><path d="M5 9.5a5 5 0 0 0 10 0M10 14.5v2.5" {...s} stroke="#fff" /></svg>
          </span>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ chips --

export function StatusChip({ status, count }: { status: string | null | undefined; count?: number }) {
  const key = (status ?? '').toUpperCase();
  const tone = key === 'APPROVED' ? 'bg-(--color-good)/10 text-(--color-good)'
    : key === 'PENDING' ? 'bg-(--color-note) text-(--color-warn)'
    : key === 'REJECTED' ? 'bg-(--color-bad)/10 text-(--color-bad)'
    : 'bg-(--color-sunken) text-(--color-ink-soft)';
  return (
    <span className={`inline-flex items-center gap-1 rounded-(--radius-chip) px-2 py-0.5 text-small font-medium ${tone}`}>
      {key === 'APPROVED' && <IconCheckCircle />}
      {key === 'PENDING' && <IconClock />}
      {key === 'REJECTED' && <IconBan />}
      {key !== 'APPROVED' && key !== 'PENDING' && key !== 'REJECTED' && <IconWarn />}
      {pretty(key || 'unknown')}{count !== undefined && count > 1 ? ` (${count})` : ''}
    </span>
  );
}

export function CategoryChip({ category }: { category: string | null | undefined }) {
  if (!category) return null;
  return (
    <span className="rounded-(--radius-chip) bg-(--color-sunken) px-1.5 py-0.5 text-micro font-semibold tracking-wide text-(--color-ink-soft) uppercase">
      {category}
    </span>
  );
}

export const pretty = (v: string) => v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();

export const LANGUAGE_LABEL: Record<string, string> = {
  en: 'English', en_US: 'English (US)', en_GB: 'English (UK)', hi: 'Hindi', mr: 'Marathi', gu: 'Gujarati',
  ta: 'Tamil', te: 'Telugu', kn: 'Kannada', ml: 'Malayalam', bn: 'Bengali', pa: 'Punjabi', ur: 'Urdu',
};
export const languageLabel = (code: string) => LANGUAGE_LABEL[code] ?? code;

// ------------------------------------------------------------------ icons --

export const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconCheckCircle = () => <svg viewBox="0 0 20 20" className="size-3.5" aria-hidden="true"><circle cx="10" cy="10" r="7" {...s} /><path d="m6.8 10.2 2.2 2.2 4.2-4.6" {...s} /></svg>;
const IconClock = () => <svg viewBox="0 0 20 20" className="size-3.5" aria-hidden="true"><circle cx="10" cy="10" r="7" {...s} /><path d="M10 6.5V10l2.4 1.6" {...s} /></svg>;
const IconBan = () => <svg viewBox="0 0 20 20" className="size-3.5" aria-hidden="true"><circle cx="10" cy="10" r="7" {...s} /><path d="m5.5 5.5 9 9" {...s} /></svg>;
const IconWarn = () => <svg viewBox="0 0 20 20" className="size-3.5" aria-hidden="true"><path d="M10 3.5 17 16H3z" {...s} /><path d="M10 8v3.5M10 13.5v.2" {...s} /></svg>;
const IconLink = () => <svg viewBox="0 0 20 20" className="size-3.5 shrink-0" aria-hidden="true"><path d="M11 4h5v5M16 4l-7 7M14 11v4H5V6h4" {...s} /></svg>;
const IconPhone = () => <svg viewBox="0 0 20 20" className="size-3.5 shrink-0" aria-hidden="true"><path d="M6.2 3.8l1.7 2.6-1.3 1.3a8.5 8.5 0 0 0 4.7 4.7l1.3-1.3 2.6 1.7-.8 2.4a1.2 1.2 0 0 1-1.3.8A12 12 0 0 1 3.2 5.3a1.2 1.2 0 0 1 .8-1.3z" {...s} /></svg>;
const IconCopy = () => <svg viewBox="0 0 20 20" className="size-3.5 shrink-0" aria-hidden="true"><path d="M8 8h6.5v6.5H8zM5.5 11.5V5.5H12" {...s} /></svg>;
const IconList = () => <svg viewBox="0 0 20 20" className="size-3.5 shrink-0" aria-hidden="true"><path d="M4 6h12M4 10h12M4 14h12" {...s} /></svg>;
const IconImage = () => <svg viewBox="0 0 24 24" className="size-8" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" {...s} /><path d="m3 16 5-5 4 4 3-3 6 5" {...s} /><circle cx="16" cy="9" r="1.5" {...s} /></svg>;
const IconVideo = () => <svg viewBox="0 0 24 24" className="size-8" aria-hidden="true"><rect x="3" y="6" width="13" height="12" rx="2" {...s} /><path d="m16 10 5-3v10l-5-3" {...s} /></svg>;
const IconDoc = () => <svg viewBox="0 0 24 24" className="size-8" aria-hidden="true"><path d="M6 3h8l5 5v13H6z" {...s} /><path d="M14 3v5h5M9 12h6M9 16h6" {...s} /></svg>;
